import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { ShiftTypeLookup } from 'src/app/perdiem/components/pay-rate/models/rate-shift-differential.model';
import { BillingPayRates } from 'src/app/perdiem/components/pay-rate/models/pay-rates.model';
import { PayRateService } from 'src/app/perdiem/components/pay-rate/service/pay-rate.services';

import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import {
    getBillingPayRates,
    getBillingPayRatesSuccess,
    getBillingPayRatesFailure,
    getIncentivePayRates,
    updateBillingPayRates,
    updateBillingPayRatesFailure,
    getShiftDiffLookupSuccess,
    getShiftDiffLookupFailure,
    getIncentivePayRatesSuccess,
    getIncentivePayRatesFailure,
    updateIncentivePayRates,
    updateIncentivePayRatesFailure,
    payRatesUpdatedMessageSuccess,
    payRatesUpdatedMessageFailure,
    loadComponent,
    updateBillingPayRatesSuccessNoUpdate,
    setFacilityId,
    incentivePayRatesUpdatedMessageFailure,
    incentivePayRatesUpdatedMessageSuccess,
    setFetchArgs,
    updateIncentivePayRatesSuccessNoUpdate,
    destroyComponent,
    payRatesUpdatingMessageSuccess,
    payRatesUpdatingMessageFailure,
    incentivePayRatesUpdatingMessageSuccess,
    incentivePayRatesUpdatingMessageFailure
} from 'src/app/root-store/perdiem-store/actions/pay-rate.actions';
import { HubConnectionService } from 'src/app/shared/services/realtime-connection.service';
import { PdHubMessage } from 'src/app/shared/services/realtime-constants';
import * as HubActions from 'src/app/root-store/hub-connection-store/hub-connection.actions';
import { selectFacilityId, selectLastFetchArgs } from 'src/app/root-store/perdiem-store/pay-rate-selectors';
import {
    ActionType,
    GenericActionData,
    setResetUserAction
} from 'src/app/root-store/perdiem-store/actions/user.actions';

export const PayRateMessages = {
    genericErrorMsg: 'A failure occurred.',
    billingRatesErrorMsg: 'An error occurred while getting PerDiem Billing Rates.',
    shiftDiffErrorMsg: 'An error occurred while getting Shift Differential Types.',
    signalRUpdateSuccessMsg: 'Pay rates have been updated in real time',
    signalRUpdateFailureMsg:
        'There was an error connecting to real time updates, please refresh the page to get the latest pay rates.'
};

@Injectable()
export class PayRateEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _payService: PayRateService,
        private readonly store: Store,
        private readonly _hubConnectionService: HubConnectionService
    ) {}

    getBillingPayRates$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getBillingPayRates),
            switchMap((action) => {
                return this._payService.getPayRates(action.facilityId).pipe(
                    map((result: BillingPayRates) => getBillingPayRatesSuccess({ billingRates: result })),
                    catchError((error) => of(getBillingPayRatesFailure({ error })))
                );
            })
        );
    });

    getBillingPayRatesFromSignalR$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(payRatesUpdatedMessageSuccess),
            concatLatestFrom(() => this.store.select(selectFacilityId)),
            switchMap(([, facilityId]) => {
                return this._payService.getPayRates(facilityId).pipe(
                    map((result: BillingPayRates) => getBillingPayRatesSuccess({ billingRates: result })),
                    catchError((error: unknown) => of(getBillingPayRatesFailure({ error })))
                );
            })
        );
    });

    getIncentivePayRates$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getIncentivePayRates),
            switchMap((action) => {
                return this._payService.getIncentivePayRates(action.facilityId, action.args).pipe(
                    map((result: BillingPayRates) => getIncentivePayRatesSuccess({ incentiveRates: result })),
                    catchError((error) => of(getIncentivePayRatesFailure({ error })))
                );
            })
        );
    });

    getIncentivePayRatesFromSignalR$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(incentivePayRatesUpdatedMessageSuccess),
            concatLatestFrom(() => [this.store.select(selectFacilityId), this.store.select(selectLastFetchArgs)]),
            switchMap(([action, facilityId, fetchArgs]) => {
                return this._payService.getIncentivePayRates(facilityId, fetchArgs).pipe(
                    map((result: BillingPayRates) => getIncentivePayRatesSuccess({ incentiveRates: result })),
                    catchError((error) => of(getIncentivePayRatesFailure({ error })))
                );
            })
        );
    });

    payratesUpdated$ = createEffect(() => {
        return this._hubConnectionService.pdHub.messagesOf<string>(PdHubMessage.RATES_UPDATED).pipe(
            map(() => payRatesUpdatedMessageSuccess()),
            catchError((error: unknown) => of(payRatesUpdatedMessageFailure({ error })))
        );
    });

    incentivePayratesUpdated$ = createEffect(() => {
        return this._hubConnectionService.pdHub.messagesOf<string>(PdHubMessage.INCENTIVE_RATES_UPDATED).pipe(
            map(() => incentivePayRatesUpdatedMessageSuccess()),
            catchError((error: unknown) => of(incentivePayRatesUpdatedMessageFailure({ error })))
        );
    });

    payratesUpdating$ = createEffect(() => {
        return this._hubConnectionService.pdHub.messagesOf<string>(PdHubMessage.RATES_UPDATING).pipe(
            map(() => payRatesUpdatingMessageSuccess()),
            catchError((error: unknown) => of(payRatesUpdatingMessageFailure({ error })))
        );
    });

    incentivePayratesUpdating$ = createEffect(() => {
        return this._hubConnectionService.pdHub.messagesOf<string>(PdHubMessage.INCENTIVE_RATES_UPDATING).pipe(
            map(() => incentivePayRatesUpdatingMessageSuccess()),
            catchError((error: unknown) => of(incentivePayRatesUpdatingMessageFailure({ error })))
        );
    });

    updateBillingPayRates$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateBillingPayRates),
            switchMap((action) => {
                return this._payService.updatePayRates(action.facilityId, action.billingRates).pipe(
                    map(() => {
                        return updateBillingPayRatesSuccessNoUpdate();
                    }),
                    catchError((error: unknown) => of(updateBillingPayRatesFailure({ error })))
                );
            })
        );
    });

    updateIncentivePayRates$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateIncentivePayRates),
            switchMap((action) => {
                return this._payService.updateIncentivePayRates(action.facilityId, action.billingRates).pipe(
                    map(() => {
                        return updateIncentivePayRatesSuccessNoUpdate();
                    }),
                    catchError((error: unknown) => of(updateIncentivePayRatesFailure({ error })))
                );
            })
        );
    });

    /**
     * Gets Shift Differential Types and then add new field:  displayName.
     * https://stackoverflow.com/questions/4149276/how-to-convert-camelcase-to-camel-case
     */
    getShiftDiffLookup$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadComponent),
            switchMap(() => {
                return this._payService.getPerDiemShiftDifferentialType().pipe(
                    map((result: ShiftTypeLookup[]) => {
                        result.forEach((item) => {
                            item.displayName = (item as any).name
                                // insert a space before all caps
                                .replace(/([A-Z])/g, ' $1')
                                // uppercase the first character
                                .replace(/^./, (str) => {
                                    return str.toUpperCase();
                                });
                        });
                        return getShiftDiffLookupSuccess({ shiftDifferentialTypes: result });
                    }),
                    catchError((error) => of(getShiftDiffLookupFailure({ error })))
                );
            })
        );
    });

    connectToSignalR$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadComponent),
            map((action) => {
                return HubActions.connectToPdHub({
                    facilityId: action.facilityId,
                    facilitySystemId: action.facilitySystemId
                });
            })
        );
    });

    setFacilityData$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadComponent),
            map((action) => {
                return setFacilityId({
                    facilityId: action.facilityId,
                    facilitySystemId: action.facilitySystemId
                });
            })
        );
    });

    setLastFetchArgs$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getIncentivePayRates),
            map((action) => {
                return setFetchArgs({
                    args: action.args
                });
            })
        );
    });

    disconnectFromSignalR$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(destroyComponent),
            map(() => {
                return HubActions.disconnectFromPdHub();
            })
        );
    });

    setResetUserAction$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(destroyComponent),
            concatLatestFrom(() => this.store.select(selectFacilityId)),
            map(([, facilityId]) => {
                return setResetUserAction({
                    actionType: ActionType.None,
                    data: { facilityId } as GenericActionData
                });
            })
        );
    });

    displaySignalRUpdateRatesSuccess$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(payRatesUpdatedMessageSuccess, incentivePayRatesUpdatedMessageSuccess),
            map(() => showToasterMessage({ message: PayRateMessages.signalRUpdateSuccessMsg, duration: 5000 }))
        );
    });

    displaySignalRUpdateRatesFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(payRatesUpdatedMessageFailure, incentivePayRatesUpdatedMessageFailure),
            map(() => showToasterMessage({ message: PayRateMessages.signalRUpdateFailureMsg, duration: 5000 }))
        );
    });

    displayFailureMessage$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getBillingPayRatesFailure, getShiftDiffLookupFailure),
            map((action) => {
                let message = PayRateMessages.genericErrorMsg;
                if (action.type.includes('Get')) {
                    message = PayRateMessages.billingRatesErrorMsg;
                } else if (action.type.includes('Lookup')) {
                    message = PayRateMessages.shiftDiffErrorMsg;
                }

                return showToasterMessage({ message: message, duration: 5000 });
            })
        );
    });
}
